import type { StringKey } from "../contract/strings";

/**
 * The Welcome walkthrough's scene as a pure function of time (#1095).
 *
 * A port of the prototype's `render(t)`: a table seen from above, three people and their
 * phones, the meeting lanes under it and the notes card. Every number here is the
 * prototype's, in its 300-wide drawing, so the apps can port the same table. `sceneAt`
 * returns what each element looks like at `t` seconds and draws nothing, so any step can be
 * shown at any moment: the tests, reduced motion and the parity gallery all ask for a
 * step's last frame directly.
 */

/** Where the whole story ends: step 5 plays from 12.4 s to here. */
export const WELCOME_END = 16.2;

/**
 * The three people, in the drawing's units: their avatar (`ax`, `ay`) and where their phone
 * lies on the table (`px`, `py`). The colours are fixed illustration colours, the one
 * exception to the theme tokens.
 */
export const PEOPLE = [
  { name: "welcome.person_1", color: "#0a6e66", ax: 72, ay: 56, px: 106, py: 112 },
  { name: "welcome.person_2", color: "#b0602f", ax: 228, ay: 56, px: 194, py: 112 },
  { name: "welcome.person_3", color: "#4a63a8", ax: 150, ay: 262, px: 150, py: 200 },
] as const satisfies ReadonlyArray<{ name: StringKey; color: string; ax: number; ay: number; px: number; py: number }>;

/** When each phone comes to the table, when it is in the meeting, and its join burst. */
const JOIN: ReadonlyArray<{ slide: number; joined: number; chirp?: readonly [number, number] }> = [
  { slide: 0.4, joined: 1.4 },
  { slide: 2.2, chirp: [3.2, 4.0], joined: 4.0 },
  { slide: 4.6, chirp: [5.6, 6.4], joined: 6.4 },
];

/** Who speaks when, in step 4. */
const SPEECH = [
  { who: 0, t0: 7.6, t1: 9.0 },
  { who: 1, t0: 9.2, t1: 10.6 },
  { who: 2, t0: 10.8, t1: 12.2 },
] as const;

/** The five steps: each plays its own stretch of the story once. */
export const CHAPTERS = [
  { t0: 0, t1: 2.4, title: "welcome.step_start", a11y: "welcome.step_start_a11y" },
  { t0: 2.4, t1: 4.8, title: "welcome.step_join", a11y: "welcome.step_join_a11y" },
  { t0: 4.8, t1: 7.2, title: "welcome.step_join_again", a11y: "welcome.step_join_again_a11y" },
  { t0: 7.2, t1: 12.4, title: "welcome.step_hear", a11y: "welcome.step_hear_a11y" },
  { t0: 12.4, t1: WELCOME_END, title: "welcome.step_notes", a11y: "welcome.step_notes_a11y" },
] as const satisfies ReadonlyArray<{ t0: number; t1: number; title: StringKey; a11y: StringKey }>;

/** The meeting's length the lanes header counts up to. */
export const MEETING_MINUTES = 46;

/** The lanes: their left edge, their width, and the stretch of time they draw. */
export const LANE_X = 64;
export const LANE_W = 224;
const TS = 7.4;
const TE = 12.2;

export const clamp = (v: number, a = 0, b = 1) => (v < a ? a : v > b ? b : v);
const ease = (x: number) => 1 - Math.pow(1 - x, 3);
const easeIO = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
/** How long a joining phone takes to glide from its owner to beside Emma's. */
const GLIDE = 1.0;
/** 0 outside [a, b], 1 inside, with `f`-second ramps at each end. */
const win = (t: number, a: number, b: number, f: number) => clamp((t - a) / f) * clamp((b - t) / f);
const xOf = (t: number) => LANE_X + clamp((t - TS) / (TE - TS)) * LANE_W;
export const laneY = (i: number) => 326 + i * 34;

/** The step `t` falls in. */
export function chapterAt(t: number): number {
  const i = CHAPTERS.findIndex((c) => t >= c.t0 && t < c.t1);
  return i < 0 ? CHAPTERS.length - 1 : i;
}

export interface PersonFrame {
  /** The phone: where it is, its size and opacity. */
  x: number;
  y: number;
  scale: number;
  opacity: number;
  /** The four level bars on its screen, as heights; shown only once it is in the meeting. */
  bars: number[];
  inMeeting: boolean;
  /** Its screen lights in the person's colour while they speak. */
  speaking: boolean;
  glow: number;
  /** The rings that ripple off the avatar while they speak. */
  ripples: Array<{ r: number; opacity: number }>;
  ring: { r: number; opacity: number };
  /** Their lane under the table. */
  lane: number;
  /** The "Joined" chip beside the phone (James and Sophie only). */
  pill: number;
}

export type LanesHeader =
  | { kind: "none" }
  | { kind: "in_meeting" }
  | { kind: "minutes"; count: number }
  | { kind: "ended"; count: number };

export interface SceneFrame {
  chapter: number;
  /** How far each progress segment is filled, 0..1. */
  progress: number[];
  opacity: number;
  people: PersonFrame[];
  tap: { r: number; opacity: number };
  beacon: Array<{ cx: number; cy: number; r: number; opacity: number }>;
  waves: Array<{ d: string; opacity: number }>;
  link: { x1: number; y1: number; x2: number; y2: number; opacity: number };
  dots: Array<{ cx: number; cy: number; opacity: number; who: number }>;
  lanes: {
    opacity: number;
    header: LanesHeader;
    /** Each speech segment's width in its lane. */
    segs: Array<{ who: number; x: number; width: number }>;
    head: { x: number; opacity: number };
  };
  notes: { opacity: number; dy: number; lines: Array<{ opacity: number; dy: number }> };
}

/** The whole scene at `t` seconds. */
export function sceneAt(t: number): SceneFrame {
  const ch = chapterAt(t);
  const progress = CHAPTERS.map((c, i) => (i < ch ? 1 : i === ch ? clamp((t - c.t0) / (c.t1 - c.t0)) : 0));

  const level = [0, 0, 0];
  for (const s of SPEECH) level[s.who] = Math.max(level[s.who], win(t, s.t0, s.t1, 0.2));
  const notesOp = ease(clamp((t - 12.8) / 0.5));

  const pos: Array<[number, number]> = PEOPLE.map((p) => [p.px, p.py]);
  const people = PEOPLE.map((p, i): PersonFrame => {
    const j = JOIN[i];
    let k: number;
    let x: number;
    let y: number;
    // Every phone fades in over the first 0.3 s of its move.
    const opacity = clamp((t - j.slide) / 0.3);
    if (j.chirp) {
      // One unbroken glide from the owner straight to beside Emma's phone, arriving as the
      // waves start, then, once linked, back to its own place at the table.
      const nx = p.px + (PEOPLE[0].px - p.px) * 0.4;
      const ny = p.py + (PEOPLE[0].py - p.py) * 0.4;
      k = easeIO(clamp((t - j.slide) / GLIDE));
      const r = easeIO(clamp((t - j.joined - 0.1) / 0.7));
      x = p.ax + (nx - p.ax) * k;
      y = p.ay + (ny - p.ay) * k;
      x += (p.px - x) * r;
      y += (p.py - y) * r;
    } else {
      k = ease(clamp((t - j.slide) / 0.6));
      x = p.ax + (p.px - p.ax) * k;
      y = p.ay + (p.py - p.ay) * k;
    }
    pos[i] = [x, y];
    const inMeeting = t >= j.joined;
    const amp = level[i];
    const bars = [0, 1, 2, 3].map(
      (n) => 2.5 + (amp * 0.92 + 0.08) * (0.5 + 0.5 * Math.sin(t * 13 + n * 1.9 + i * 2.3)) * 14
    );
    let glow = 0.55 * amp;
    glow = Math.max(glow, 0.7 * win(t, j.joined, j.joined + 0.9, 0.2));
    if (t > 12.8) glow = Math.max(glow, (0.16 + 0.1 * Math.sin(t * 3 + i * 2)) * notesOp);
    const sp = SPEECH.find((s) => s.who === i && t >= s.t0 && t <= s.t1);
    const ripples = [0, 1, 2].map((n) => {
      if (!sp) return { r: 19, opacity: 0 };
      const ph = ((t - sp.t0) * 1.3 + n / 3) % 1;
      return { r: 19 + ph * 36, opacity: (1 - ph) * 0.5 * win(t, sp.t0, sp.t1, 0.2) };
    });
    return {
      x,
      y,
      scale: 0.6 + 0.4 * k,
      opacity,
      bars,
      inMeeting,
      speaking: amp > 0.4,
      glow,
      ripples,
      ring: { r: 21 + 1.5 * Math.sin(t * 10), opacity: amp },
      lane: ease(clamp((t - j.joined) / 0.4)),
      pill: i === 0 ? 0 : win(t, j.joined, j.joined + 1.4, 0.25),
    };
  });

  // Step 1: Emma taps Start, and her phone softly signals that the meeting is open.
  const tp = clamp((t - 1.1) / 0.5);
  const tap = { r: 4 + tp * 16, opacity: tp > 0 && tp < 1 ? (1 - tp) * 0.8 : 0 };

  // Steps 2 and 3: the new phone comes close to Emma's, catches her phone's waves, and the
  // two link.
  let jn = 0;
  for (const i of [1, 2]) {
    const c = JOIN[i].chirp!;
    if (t >= c[0] - 0.15 && t <= c[1] + 0.15) jn = i;
  }
  const beacon = [0, 1].map((n) => {
    const ph = (t * 0.45 + n / 2) % 1;
    return {
      cx: pos[0][0],
      cy: pos[0][1],
      r: 16 + ph * 24,
      opacity: (1 - ph) * 0.28 * win(t, 1.4, 7.2, 0.4) * (jn ? 0.2 : 1),
    };
  });
  const waves = [0, 1, 2, 3].map((n) => {
    if (!jn) return { d: "", opacity: 0 };
    const c = JOIN[jn].chirp!;
    const a = pos[0];
    const b = pos[jn];
    const D = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const th = Math.atan2(b[1] - a[1], b[0] - a[0]);
    const ph = ((((t - c[0]) * 1.5 + n / 4) % 1) + 1) % 1;
    const r = 13 + ph * Math.max(0, D - 26);
    const spread = 0.35 + ph * 0.3;
    const pt = (ang: number) => `${(a[0] + r * Math.cos(ang)).toFixed(1)} ${(a[1] + r * Math.sin(ang)).toFixed(1)}`;
    return {
      d: `M${pt(th - spread)} A${r.toFixed(1)} ${r.toFixed(1)} 0 0 1 ${pt(th + spread)}`,
      opacity: Math.sin(Math.PI * ph) * 0.85 * win(t, c[0], c[1], 0.15),
    };
  });
  const lk = [1, 2].find((i) => t >= JOIN[i].joined - 0.2 && t <= JOIN[i].joined + 0.6);
  const link = lk
    ? {
        x1: pos[0][0],
        y1: pos[0][1],
        x2: pos[lk][0],
        y2: pos[lk][1],
        opacity: win(t, JOIN[lk].joined - 0.2, JOIN[lk].joined + 0.6, 0.2),
      }
    : { x1: 0, y1: 0, x2: 0, y2: 0, opacity: 0 };

  // Step 4: each voice flows from its own phone into its own lane.
  const speaker = SPEECH.find((s) => t >= s.t0 && t <= s.t1);
  const dots = [0, 1, 2].map((n) => {
    if (!speaker) return { cx: 0, cy: 0, opacity: 0, who: 0 };
    const src = PEOPLE[speaker.who];
    const ex = xOf(t);
    const ey = laneY(speaker.who) + 7;
    const u = ((t - speaker.t0) * 1.1 + n / 3) % 1;
    return {
      cx: (1 - u * u) * src.px + u * u * ex,
      cy: (1 - u) * (1 - u) * src.py + 2 * (1 - u) * u * 300 + u * u * ey,
      opacity: Math.sin(Math.PI * u) * 0.9 * win(t, speaker.t0, speaker.t1, 0.2),
      who: speaker.who,
    };
  });

  const header: LanesHeader =
    t < 1.4
      ? { kind: "none" }
      : t < TS
        ? { kind: "in_meeting" }
        : t < TE
          ? { kind: "minutes", count: Math.max(1, Math.floor(clamp((t - TS) / (TE - TS)) * MEETING_MINUTES)) }
          : { kind: "ended", count: MEETING_MINUTES };

  return {
    chapter: ch,
    progress,
    opacity: clamp(t / 0.4),
    people,
    tap,
    beacon,
    waves,
    link,
    dots,
    lanes: {
      opacity: 1 - clamp((t - 12.4) / 0.4),
      header,
      segs: SPEECH.map((s) => ({
        who: s.who,
        x: xOf(s.t0),
        width: t < s.t0 ? 0 : Math.max(0, xOf(Math.min(t, s.t1)) - xOf(s.t0)),
      })),
      head: { x: xOf(Math.min(t, TE)), opacity: t > TS && t < TE ? 1 : 0 },
    },
    // Step 5: the meeting ends and the notes arrive.
    notes: {
      opacity: notesOp,
      dy: (1 - notesOp) * 16,
      lines: [0, 1, 2].map((n) => {
        const v = ease(clamp((t - 13.1 - n * 0.35) / 0.3));
        return { opacity: v, dy: (1 - v) * 5 };
      }),
    },
  };
}
